import { useEffect, useState } from "react";
import {
  listUsers,
  blockUser,
  unblockUser,
  deleteUser,
  canManageRole,
  AdminUser,
} from "../../../Services/Admin/users";
import { useAuth } from "../../../Services/AuthContext";
import "./UsersPanel.css";

/** reloadKey ändras av föräldern när ett konto skapats, så listan hämtas om. */
export default function UsersPanel({ reloadKey = 0 }: { reloadKey?: number }) {
  const { user: me } = useAuth();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    let alive = true;

    const t = setTimeout(() => {
      setLoading(true);
      setError(null);

      listUsers({ q: q.trim() || undefined, page: 1 })
        .then((data) => {
          if (!alive) return;
          setUsers(data as AdminUser[]);
        })
        .catch((e) => {
          if (!alive) return;
          setError(e?.message ?? "Failed to load users");
        })
        .finally(() => {
          if (!alive) return;
          setLoading(false);
        });
    }, 250);

    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [q, reloadKey]);

  const onBlockToggle = async (u: AdminUser) => {
    if (!u.blocked) {
      const confirmed = window.confirm(
        `Block ${u.email}?\n\nThey will be unable to sign in until an administrator unblocks the account.`
      );
      if (!confirmed) return;
    }
    setUsers(prev => prev.map(x => x.id === u.id ? { ...x, blocked: !x.blocked } : x));

    try {
      if (u.blocked) await unblockUser(u.id);
      else await blockUser({ userId: u.id, reason: "Blocked by admin" });
    } catch (e: any) {
      setUsers(prev => prev.map(x => x.id === u.id ? { ...x, blocked: u.blocked } : x));
      setError(e?.message ?? "Failed to update user");
    }
  };

  const onDelete = async (u: AdminUser) => {
    const confirmed = window.confirm(
      `Delete ${u.email}?\n\nThe account and everything it has posted are removed permanently. This cannot be undone.`
    );
    if (!confirmed) return;

    try {
      await deleteUser(u.id);
      setUsers(prev => prev.filter(x => x.id !== u.id));
    } catch (e: any) {
      setError(e?.message ?? "Failed to delete user");
    }
  };

  return (
    <div className="admin-panel">
      <div className="admin-toolbar">
        <input
          className="admin-toolbar-grow"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search email / name…"
        />
      </div>

      {error && <div className="admin-error">{error}</div>}
      {loading && <p className="admin-hint">Loading…</p>}

      {!loading && users.length === 0 && (
        <p className="admin-empty">No users match that search.</p>
      )}

      <div className="users-table-wrap">
        <table className="admin-table users-table">
          <thead>
            <tr><th>User</th><th>Role</th><th>Status</th><th>Created</th><th><span className="sr-only">Actions</span></th></tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id}>
                <td>
                  <div className="user-main">
                    <strong>{[u.firstName, u.lastName].filter(Boolean).join(" ") || "Unnamed user"}</strong>
                    <span>{u.email}</span>
                  </div>
                </td>
                <td><span className="admin-role-badge">{u.role.toLowerCase()}</span></td>
                <td><span className={`admin-status admin-status--${u.blocked ? "blocked" : "active"}`}>{u.blocked ? "Blocked" : "Active"}</span></td>
                <td>{u.createdAt ? new Date(u.createdAt).toLocaleDateString() : "—"}</td>
                <td className="users-action-cell">
                  {u.id !== me?.id && canManageRole(me?.role, u.role) && (
                    <>
                      <button className={`block-button${u.blocked ? "" : " admin-btn--danger"}`} onClick={() => onBlockToggle(u)}>
                        {u.blocked ? "Unblock" : "Block"}
                      </button>
                      <button className="block-button admin-btn--danger" onClick={() => onDelete(u)}>
                        Delete
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
